import React from 'react';
import { plural } from '@lingui/macro';

import RatingsReview from '@rover/components/src/RatingsReview/RatingsReview';
import Map from '@rover/icons/svg/map.svg';
import Rebook from '@rover/icons/svg/rebook.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Flex, Text } from '@rover/kibble/core';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';

import getSitterLocation from '../utils/getSitterLocation';

const ICON_SIZE = 16;

type Props = {
  repeatClientCount: SearchResult['providerProfile']['repeatClientCount'];
} & Pick<
  SearchResult,
  | 'reviewsCount'
  | 'yearsOfExperience'
  | 'ratingsAverage'
  | 'neighborhood'
  | 'city'
  | 'state'
  | 'zip'
>;

const SitterStatsSection: React.FC<Props> = ({
  reviewsCount,
  repeatClientCount,
  yearsOfExperience,
  ratingsAverage,
  neighborhood,
  city,
  state,
  zip,
}: Props) => {
  const location = getSitterLocation(neighborhood, city, state, zip);

  const shouldShowRatingsReviews = reviewsCount > 0 && parseFloat(ratingsAverage) > 0;
  const shouldShowRepeatCount = repeatClientCount > 0;
  const shouldShowYearsOfExperience = yearsOfExperience !== '';
  const shouldShowLocation = location !== '';

  return (
    <Flex flexDirection="column" gap="1x" flex="1" minHeight="82px">
      {/* minHeight calculated by lineHeight * numOfRows + (flexGap * (numOfRows - 1)) */}
      {/*                            17.5          4        1x => 4px         3        */}
      {/* 17.5*4 + 4*3 => 82px */}
      {/* 1. Reviews and count */}
      {shouldShowRatingsReviews && (
        <RatingsReview
          fontWeight="regular"
          ratingsAverage={ratingsAverage}
          reviewsCount={reviewsCount}
        />
      )}
      {/* 2. Repeat client */}
      {shouldShowRepeatCount && (
        <Flex gap="1x" flexBasis="auto" data-testid="repeat-client-count-row">
          <Rebook height={ICON_SIZE} width={ICON_SIZE} />
          <Text textColor="primary" size="100">
            {plural(repeatClientCount, {
              one: '# repeat client',
              other: '# repeat clients',
            })}
          </Text>
        </Flex>
      )}
      {/* 3. Years of experience */}
      {shouldShowYearsOfExperience && (
        <Flex gap="1x" data-testid="years-of-experience-row">
          <Trophy width={ICON_SIZE} height={ICON_SIZE} />
          {/* Validate that years of experience is a string and not just a numeric value */}
          <Text textColor="primary" size="100">
            {yearsOfExperience}
          </Text>
        </Flex>
      )}
      {/* 4. Location */}
      {shouldShowLocation && (
        <Flex gap="1x" data-testid="location-row">
          <Map width={ICON_SIZE} height={ICON_SIZE} />
          <Text textColor="primary" size="100">
            {location}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default SitterStatsSection;
